import { faChevronDown } from '@fortawesome/pro-regular-svg-icons/faChevronDown';
import { faChevronUp } from '@fortawesome/pro-regular-svg-icons/faChevronUp';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import useApi from '@propertypal/shared/src/hooks/useApi';
import { trackGaEvent } from '@propertypal/shared/src/services/analytics';
import { Agent } from '@propertypal/shared/src/types/agents';
import { RecommendedAgent, ValuationResult } from '@propertypal/shared/src/types/valuations';
import Button from '@propertypal/web-ui/src/buttons/Button';
import Checkbox from '@propertypal/web-ui/src/inputs/Checkbox';
import { Row } from '@propertypal/web-ui/src/layout';
import { SubHeading, Text } from '@propertypal/web-ui/src/typography';
import React, { FunctionComponent, useEffect, useState } from 'react';
import { AgentBox, CheckRow, MoreAgentBox, SubmitButton, ToggleAgentsButton } from './EstateagentsSelect.style';

interface Props {
  mode: string;
  result: ValuationResult;
  onAgentsSelect: (agents: Agent[]) => void;
  onRequestValuation: (agents: Agent[]) => void;
}

const EstateAgentsSelect: FunctionComponent<Props> = (props) => {
  const initiallySelected = props.result.promotedAgents?.map((agent) => agent.account);
  const [agents, setAgents] = useState<Agent[]>(initiallySelected || []);
  const [showMore, setShowMore] = useState<boolean>(false);
  const [data] = useApi<RecommendedAgent[]>(`/valuation/${props.result.id}/more-agents`);
  const preSelected = data?.filter((agent) => agent.showLogo);
  const standardAgents = data?.filter((agent) => !agent.showLogo);

  const toggleAgent = (agent: Agent) => {
    const index = agents.indexOf(agent);
    trackGaEvent('valuation_change_selection', { type: props.mode });

    if (index >= 0) {
      const mAgents = agents.filter((a) => a !== agent);
      setAgents(mAgents);
    } else {
      setAgents([...agents, agent]);
    }
  };

  useEffect(() => {
    props.onAgentsSelect(agents);
  }, [agents]);

  return (
    <>
      <SubHeading>Select the experts agents you would like to contact</SubHeading>

      <Text mt={15} mb={15}>
        We recommend contacting at least <b>3 agents</b> so you can compare their valuations.
      </Text>

      {props.result.promotedAgents?.map((agent) => (
        <CheckRow key={agent.account.accountNumber}>
          <Checkbox
            logo={agent.account.logo?.standard}
            label={agent.account.organisation}
            name={agent.account.accountNumber}
            onValueChange={() => toggleAgent(agent.account)}
            value={agents.includes(agent.account)}
            testID={`agentSelect${agent.account.accountNumber}`}
          />
        </CheckRow>
      ))}

      {!!data && (
        <>
          {!showMore && (
            <ToggleAgentsButton
              onClick={() => {
                trackGaEvent('valuation_more_agents_click', { type: props.mode });
                setShowMore(true);
              }}
              data-testid="showMore"
            >
              Show more agents <FontAwesomeIcon icon={faChevronDown} />
            </ToggleAgentsButton>
          )}

          {showMore && (
            <ToggleAgentsButton onClick={() => setShowMore(false)} data-testid="showLess">
              Show fewer agents <FontAwesomeIcon icon={faChevronUp} />
            </ToggleAgentsButton>
          )}

          {showMore && (
            <>
              {preSelected?.map((agent) => (
                <CheckRow key={agent.account.accountNumber}>
                  <Checkbox
                    logo={agent.account.logo?.standard}
                    label={agent.account.organisation}
                    name={agent.account.accountNumber}
                    onValueChange={() => toggleAgent(agent.account)}
                    value={agents.includes(agent.account)}
                    testID={`agentSelect${agent.account.accountNumber}`}
                  />
                </CheckRow>
              ))}

              <MoreAgentBox>
                {standardAgents?.map((agent) => (
                  <Row key={agent.account.accountNumber} m={15}>
                    <Checkbox
                      name={agent.account.accountNumber}
                      onValueChange={() => toggleAgent(agent.account)}
                      value={agents.includes(agent.account)}
                      testID={`agentSelect${agent.account.accountNumber}`}
                    />

                    <AgentBox>
                      <Text>{agent.account.organisation}</Text>
                    </AgentBox>
                  </Row>
                ))}
              </MoreAgentBox>
            </>
          )}
        </>
      )}

      <SubmitButton>
        <Button
          containerStyle={{ marginTop: 15 }}
          onClick={() => props.onRequestValuation(agents)}
          disabled={agents.length === 0}
        >
          Request an Agent Valuation
        </Button>
      </SubmitButton>
    </>
  );
};

export default EstateAgentsSelect;
